import axios from 'axios';
import logger from '../utils/logger';

// Use relative path so nginx can proxy to the backend
const API_BASE_URL = '/api';

// Debug log for API initialization
logger.info('Initializing API with base URL:', { baseUrl: API_BASE_URL });

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Adding timeout and validateStatus for better error handling
  timeout: 30000,
  // Enable cross-origin credentials
  withCredentials: true,
  validateStatus: (status) => {
    logger.debug('API Response status:', { status });
    // Accept 2xx and 3xx, reject 401 and 422 so they reach the error
    // interceptor for token-expiry detection. Other 4xx/5xx also rejected.
    return status >= 200 && status < 400;
  }
});

// Tokens are now HttpOnly cookies — no manual Authorization header needed.
// The browser sends cookies automatically with every request (withCredentials: true).

// Helper function to redirect to login on auth expiration
const handleAuthExpired = () => {
  // On MFA/auth pages, don't clear sessionStorage — temp tokens live there
  if (window.location.pathname.startsWith('/mfa-verify') ||
      window.location.pathname.startsWith('/mfa-setup') ||
      window.location.pathname.startsWith('/login') ||
      window.location.pathname.startsWith('/signup') ||
      window.location.pathname.startsWith('/reset-password') ||
      window.location.pathname.startsWith('/verify-email') ||
      window.location.pathname.startsWith('/oauth') ||
      window.location.pathname.startsWith('/pending')) {
    return;
  }
  cancelProactiveRefresh();
  sessionStorage.clear();
  window.location.href = '/login';
};

// Token refresh state — single mutex for ALL refresh paths
let isRefreshing = false;
let refreshPromise = null;      // Shared in-flight promise (deduplicates concurrent calls)
let refreshSubscribers = [];
let proactiveRefreshTimer = null;

// Decode JWT payload to get expiry time (seconds since epoch)
// Schedule a proactive token refresh. Tokens are HttpOnly cookies —
// we refresh periodically to keep the session alive while the tab is open.
const scheduleProactiveRefresh = () => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }

  // Refresh every 12 minutes (access token is 15 min)
  const delay = 12 * 60 * 1000;
  proactiveRefreshTimer = setTimeout(async () => {
    logger.info('Proactive token refresh triggered');
    const result = await refreshAccessToken();
    if (result) {
      scheduleProactiveRefresh();
    } else {
      logger.warn('Proactive token refresh failed');
    }
  }, delay);
};

const cancelProactiveRefresh = () => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
};

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers = [];
};

// Attempt to refresh the access token. Refresh token is sent automatically
// via HttpOnly cookie (withCredentials: true). New tokens are set as cookies
// by the backend response — no localStorage needed.
const refreshAccessToken = () => {
  if (refreshPromise) {
    logger.debug('Refresh already in-flight — reusing existing promise');
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = api.post('/auth/refresh')
    .then((response) => {
      if (response.data?.status === 'success') {
        logger.info('Access token refreshed successfully');
        return true;
      }
      return null;
    })
    .catch((error) => {
      logger.error('Failed to refresh access token', { error: error.message });
      return null;
    })
    .finally(() => {
      refreshPromise = null;
      isRefreshing = false;
    });

  return refreshPromise;
};

// Proactive refresh is started by AuthContext after successful profile fetch

// Check if response indicates token expiry/invalid
const isTokenExpired = (response) => {
  const status = response.status;
  const data = response.data;
  const msg = data?.msg || data?.error || data?.message || '';
  const msgLower = msg.toLowerCase();
  
  // If the request was to login, register, verify MFA, WebAuthn enrollment, or profile on auth pages, a 401 is NOT a token expiry
  const url = response.config?.url || '';
  if (url.includes('/auth/login') || url.includes('/auth/signup') || url.includes('/auth/mfa/verify') || url.includes('/auth/mfa/enable') || url.includes('/auth/webauthn/register') || url.includes('/profile')) {
    return false;
  }
  
  // Check for common JWT expiry indicators
  if (status === 401) {
    // If message is about invalid credentials, it's not a token expiry issue
    if (msgLower.includes('password') || msgLower.includes('credentials') || msgLower.includes('invalid email') || msgLower.includes('incorrect')) {
      return false;
    }
    return true;
  }
  if (status === 422 && (msgLower.includes('token') || msgLower.includes('signature') || msgLower.includes('expired'))) return true;
  if (msgLower.includes('token has expired')) return true;
  if (msgLower.includes('signature has expired')) return true;
  if (msgLower.includes('invalid token')) return true;
  if (msgLower.includes('token is invalid')) return true;
  if (msgLower.includes('not enough segments')) return true;
  
  return false;
};

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    logger.debug('API Response received', {
      url: response.config.url,
      method: response.config.method,
      status: response.status
    });
    
    // Note: 401 responses are excluded from validateStatus and handled
    // by the error interceptor which attempts silent token refresh.
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't log expected auth failures (wrong MFA code, bad credentials) as errors
    const errUrl = error.config?.url || '';
    const isExpectedAuthFailure =
      error.response?.status === 401 &&
      (errUrl.includes('/auth/mfa/verify') ||
       errUrl.includes('/auth/mfa/enable') ||
       errUrl.includes('/auth/login') ||
       errUrl.includes('/auth/webauthn/authenticate') ||
       errUrl.includes('/auth/webauthn/register'));

    if (!isExpectedAuthFailure) {
      logger.error('API Error occurred', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        code: error.code
      });
    }

    if (error.response) {
      // Check if it's a token expiry issue and we haven't already tried to refresh
      if (isTokenExpired(error.response) && !originalRequest._retry) {
        // Don't try to refresh if this was the refresh request itself
        if (originalRequest.url?.includes('/auth/refresh')) {
          handleAuthExpired();
          return Promise.reject(error);
        }
        
        // If a refresh is already in-flight, queue this request to retry
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeTokenRefresh(() => {
              originalRequest._retry = true;
              resolve(api(originalRequest));
            });
          });
        }
        
        originalRequest._retry = true;
        
        try {
          const refreshed = await refreshAccessToken();
          
          if (refreshed) {
            onTokenRefreshed(true);
            scheduleProactiveRefresh();
            return api(originalRequest);
          } else {
            onRefreshFailed();
            handleAuthExpired();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          onRefreshFailed();
          handleAuthExpired();
          return Promise.reject(refreshError);
        }
      }
      
      // Handle other specific error cases (non-auth errors)
      switch (error.response.status) {
        case 403:
          // Forbidden - this is access denied, not token expiry
          logger.error('Access forbidden', {
            url: error.config?.url,
            method: error.config?.method
          });
          break;
        case 404:
          logger.error('API endpoint not found', {
            url: error.config?.url,
            method: error.config?.method
          });
          break;
        case 500:
          logger.error('Server error occurred', {
            url: error.config?.url,
            method: error.config?.method,
            serverError: error.response?.data
          });
          break;
        default:
          break;
      }
    } else if (error.request) {
      // Request was made but no response received
      logger.error('No response received from server', {
        url: error.config?.url,
        method: error.config?.method,
        code: error.code
      });
    } else {
      // Error in request configuration
      logger.error('Request configuration error', {
        message: error.message,
        config: error.config
      });
    }
    return Promise.reject(error);
  }
);

export { api, scheduleProactiveRefresh, cancelProactiveRefresh };

export const portalAPI = {
  getPortals: async () => {
    try {
      const response = await api.get('/portals');
      
      if (response.data && typeof response.data === 'object') {
        if (response.data.status === 'error') {
          throw new Error(response.data.error || 'Failed to fetch portals');
        }
        return response.data;
      } else {
        console.error('Unexpected response format:', response.data);
        throw new Error('Invalid response format from portal API');
      }
    } catch (error) {
      console.error('Failed to fetch portals:', error);
      throw error.response?.data || error;
    }
  },

  deletePortal: async (portalId) => {
    try {
      const response = await api.delete(`/portals/${portalId}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to delete portal');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to delete portal:', error);
      throw error.response?.data || error;
    }
  },

  disassociatePortal: async (portalId) => {
    try {
      const response = await api.post(`/portals/${portalId}/disassociate`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to remove portal access');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to remove portal access:', error);
      throw error.response?.data || error;
    }
  },

  createPersonalPortal: async (portalData) => {
    try {
      const response = await api.post('/portals/personal', portalData);
      return response.data;
    } catch (error) {
      console.error('Failed to create personal portal:', error);
      throw error.response?.data || error;
    }
  },

  getPortalSettings: async (portalId) => {
    try {
      const response = await api.get(`/portals/${portalId}/settings`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch portal settings');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch portal settings:', error);
      throw error.response?.data || error;
    }
  },

  updatePortalSettings: async (portalId, settings) => {
    try {
    const url = `/portals/${portalId}/settings`;
    const postBody = settings[0] ? {
      name: settings[0].name,
      value: settings[0].value
    } : {};
    const response = await api.post(url, postBody);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update portal settings');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update portal settings:', error);
      throw error.response?.data || error;
    }
  },

  // New method for getting a specific portal setting
  getPortalSetting: async (portalId, settingId) => {
    try {
      const response = await api.get(`/portals/${portalId}/settings/${settingId}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || `Failed to fetch setting ${settingId}`);
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch setting ${settingId} for portal ${portalId}:`, error);
      throw error.response?.data || error;
    }
  },

  // Method for updating a specific portal setting
  updatePortalSetting: async (portalId, settingId, value) => {
    try {
      const id = parseInt(settingId, 10);
      // According to the updated API spec, we only need to send the value
      // The setting_id in the URL is now expected to be an integer
      const response = await api.put(`/portals/${portalId}/settings/${id}`, { 
        value: value 
      });
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || `Failed to update setting ${settingId}`);
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to update setting ${settingId} for portal ${portalId}:`, error);
      throw error.response?.data || error;
    }
  },

  // New method for deleting a specific portal setting
  deletePortalSetting: async (portalId, settingId) => {
    try {
      const id = parseInt(settingId, 10);
      const response = await api.delete(`/portals/${portalId}/settings/${id}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || `Failed to delete setting ${settingId}`);
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to delete setting ${settingId} for portal ${portalId}:`, error);
      throw error.response?.data || error;
    }
  }
};

export const adminAPI = {
  getUsers: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.search) query.set('search', params.search);
      if (params.permission_level) query.set('permission_level', params.permission_level);
      if (params.company_id) query.set('company_id', params.company_id);
      if (params.page) query.set('page', params.page);
      if (params.per_page) query.set('per_page', params.per_page);
      const qs = query.toString();
      const response = await api.get(`/admin/users${qs ? `?${qs}` : ''}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch users');
      }
      return {
        status: response.data.status,
        users: response.data.users || [],
        total: response.data.total,
        page: response.data.page,
        per_page: response.data.per_page,
      };
    } catch (error) {
      logger.error('Failed to fetch users:', error);
      throw error.response?.data || error;
    }
  },

  getGlobalAdmins: async () => {
    try {
      const response = await api.get('/admin/global-admins');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch global admins');
      }
      return {
        status: response.data.status,
        global_admins: response.data.global_admins || [],
        count: response.data.count,
      };
    } catch (error) {
      logger.error('Failed to fetch global admins:', error);
      throw error.response?.data || error;
    }
  },

  createUser: async (userData) => {
    try {
      const response = await api.post('/admin/users', userData);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to create user');
      }
      return {
        status: response.data.status,
        user: response.data.user,
        message: response.data.message
      };
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error.response?.data || error;
    }
  },

  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update user');
      }
      return {
        status: response.data.status,
        user: response.data.user,
        message: response.data.message
      };
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error.response?.data || error;
    }
  },

  getUserAttributes: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/attributes`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch attributes');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch attributes:', error);
      throw error.response?.data || error;
    }
  },

  saveUserAttribute: async (userId, attributeName, attributeValue) => {
    try {
      const response = await api.post(`/admin/users/${userId}/attributes`, {
        attribute_name: attributeName,
        attribute_value: attributeValue
      });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to save attribute');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to save attribute:', error);
      throw error.response?.data || error;
    }
  },

  deleteUserAttribute: async (userId, attributeName) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/attributes/${attributeName}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to delete attribute');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to delete attribute:', error);
      throw error.response?.data || error;
    }
  },

  getUserApplications: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/applications`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch user applications');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get user applications:', error);
      throw error.response?.data || error;
    }
  },

  getAdminRoles: async () => {
    try {
      const response = await api.get('/admin/roles');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch roles');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get admin roles:', error);
      throw error.response?.data || error;
    }
  },

  getUserCompanies: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/companies`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch user companies');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get user companies:', error);
      throw error.response?.data || error;
    }
  },

  getUserCompanyRoles: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/company-roles`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch user company roles');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get user company roles:', error);
      throw error.response?.data || error;
    }
  },

  updateUserCompanyRoles: async (userId, companyId, roleIds) => {
    try {
      const response = await api.put(`/admin/users/${userId}/companies/${companyId}/roles`, { role_ids: roleIds });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update roles');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update user company roles:', error);
      throw error.response?.data || error;
    }
  },

  addUserCompany: async (userId, companyId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/companies`, { company_id: companyId });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to add company');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to add user company:', error);
      throw error.response?.data || error;
    }
  },

  removeUserCompany: async (userId, companyId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/companies/${companyId}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to remove company');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to remove user company:', error);
      throw error.response?.data || error;
    }
  },

  setPrimaryCompany: async (userId, companyId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/companies/${companyId}/primary`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to set primary company');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to set primary company:', error);
      if (error.response?.status === 405) {
        throw new Error(`Method not allowed (405) — URL: /admin/users/${userId}/companies/${companyId}/primary`);
      }
      throw error.response?.data || error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to delete user');
      }
      return {
        status: response.data.status,
        message: response.data.message
      };
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error('Cannot delete your own account');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      console.error('Failed to delete user:', error);
      throw error.response?.data || error;
    }
  },

  sendWelcomeEmail: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/send-welcome-email`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to send welcome email');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to send welcome email:', error);
      throw error.response?.data || error;
    }
  },

  startImpersonation: async (userId, companyId = null) => {
    try {
      const body = companyId ? { company_id: companyId } : {};
      const response = await api.post(`/admin/users/${userId}/impersonate`, body);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to start impersonation');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      throw error.response?.data || error;
    }
  },

  stopImpersonation: async () => {
    try {
      const response = await api.post('/admin/impersonate/stop');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to stop impersonation');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      throw error.response?.data || error;
    }
  },

  getAuditEvents: async () => {
    try {
      const response = await api.get('/admin/audit/events');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch audit events');
      }
      return {
        status: response.data.status,
        events: response.data.events || []
      };
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error('Admin access required');
      }
      console.error('Failed to fetch audit events:', error);
      throw error.response?.data || error;
    }
  },

  exportAuditEvents: async (format = 'csv', filters = {}) => {
    try {
      const params = new URLSearchParams({ format, ...filters });
      const response = await api.get(`/admin/audit/events/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      link.setAttribute('download', filenameMatch ? filenameMatch[1] : `audit_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export audit events:', error);
      throw error.response?.data || error;
    }
  },

  // New method for getting portal settings (admin access)
  getPortalSettings: async (portalId) => {
    try {
      const response = await api.get(`/admin/portal/${portalId}/settings`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch portal settings');
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch admin settings for portal ${portalId}:`, error);
      throw error.response?.data || error;
    }
  },

  // Method for updating a specific portal setting (admin access)
  updatePortalSetting: async (portalId, settingId, value) => {
    try {
      // Only send the value for update
      const response = await api.put(`/admin/portal/${portalId}/settings/${settingId}`, { 
        value: value 
      });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || `Failed to update setting ${settingId}`);
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to update admin setting ${settingId} for portal ${portalId}:`, error);
      throw error.response?.data || error;
    }
  },

  // New method for deleting a specific portal setting (admin access)
  deletePortalSetting: async (portalId, settingId) => {
    try {
      const response = await api.delete(`/admin/portal/${portalId}/settings/${settingId}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || `Failed to delete setting ${settingId}`);
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to delete admin setting ${settingId} for portal ${portalId}:`, error);
      throw error.response?.data || error;
    }
  },
  
  // Method for creating a new portal setting (admin access)
  createPortalSetting: async (portalId, name, value) => {
    try {
      const response = await api.post(`/admin/portal/${portalId}/settings`, {
        name,
        value
      });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to create portal setting');
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to create admin setting for portal ${portalId}:`, error);
      throw error.response?.data || error;
    }
  },

  setUserLifecycle: async (userId, lifecycleState) => {
    try {
      const response = await api.put(`/admin/users/${userId}/lifecycle`, {
        lifecycle_state: lifecycleState
      });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update lifecycle state');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to set user lifecycle:', error);
      throw error.response?.data || error;
    }
  },

  getPermissionPreview: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/permission-preview`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch permission preview');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch permission preview:', error);
      throw error.response?.data || error;
    }
  },

  // Service Type Presets
  listPresets: async (serviceType = null) => {
    try {
      const params = serviceType ? `?service_type=${encodeURIComponent(serviceType)}` : '';
      const response = await api.get(`/admin/service-type-presets${params}`);
      return response.data;
    } catch (error) {
      logger.error('Failed to list presets:', error);
      throw error.response?.data || error;
    }
  },

  setPresets: async (serviceType, presets) => {
    try {
      const response = await api.put('/admin/service-type-presets', { service_type: serviceType, presets });
      return response.data;
    } catch (error) {
      logger.error('Failed to set presets:', error);
      throw error.response?.data || error;
    }
  }
};

export const auditAPI = {
  getEvents: async () => {
    try {
      const response = await api.get('/audit/events');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch audit events');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user audit events:', error);
      throw error.response?.data || error;
    }
  }
};

export const profileAPI = {
  getProfile: async () => {
    try {
      const response = await api.get('/profile');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch profile');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      throw error.response?.data || error;
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/profile', profileData);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update profile');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error.response?.data || error;
    }
  },

  uploadProfilePicture: async (formData) => {
    try {
      const response = await api.post('/profile/picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to upload profile picture');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      throw error.response?.data || error;
    }
  },

  updateTheme: async (theme) => {
    try {
      const response = await api.put('/profile/theme', { theme });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update theme');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update theme:', error);
      throw error.response?.data || error;
    }
  },

  setPrimaryCompany: async (companyId) => {
    try {
      const response = await api.put(`/profile/company/${companyId}/set-primary`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to set primary company');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to set primary company:', error);
      throw error.response?.data || error;
    }
  },

  updatePreferences: async (preferences) => {
    try {
      const response = await api.put('/profile/preferences', preferences);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update preferences');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update preferences:', error);
      throw error.response?.data || error;
    }
  },

  removeProfilePicture: async () => {
    try {
      const response = await api.delete('/profile/picture');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to remove profile picture');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to remove profile picture:', error);
      throw error.response?.data || error;
    }
  }
};

export const authAPI = {
  login: async (email, password) => {
    try {
      logger.debug('Attempting login');

      const response = await api.post('/auth/login', { email, password });
      
      // If the API returns an error status, throw it as an error
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Login failed');
      }

      // If MFA is required, validate and return response according to schema
      if (response.data.mfa_required === true) {
        if (!response.data.mfa_token) {
          throw new Error('MFA token missing in response');
        }
        return {
          status: response.data.status,
          mfa_required: true,
          mfa_token: response.data.mfa_token
        };
      }

      logger.debug('Login response received', {
        status: response.status,
        responseStatus: response.data.status
      });
      
      return response.data;
    } catch (error) {
      logger.error('Login error occurred', {
        status: error.response?.status,
        code: error.code
      });

      // Enhanced error handling with better messages
      switch (error.code) {
        case 'ECONNREFUSED':
          throw new Error('Unable to connect to the server. Please check if the server is running.');
        case 'ETIMEDOUT':
          throw new Error('The request timed out. Please try again.');
        case 'ERR_NETWORK':
          throw new Error('Network error. Please check your internet connection.');
        default:
          if (error.response) {
            switch (error.response.status) {
              case 401:
                throw new Error(error.response.data?.error || 'Invalid email or password.');
              case 400:
                throw new Error(error.response.data?.error || 'Invalid input data.');
              case 403:
                throw new Error(error.response.data?.error || 'Access denied. Please check your credentials.');
              case 500:
                throw new Error('Server error. Please try again later.');
              case 502:
              case 503:
              case 504:
                throw new Error('Server is temporarily unavailable. Please try again later.');
              default:
                throw error.response.data?.error || new Error('An unexpected error occurred.');
            }
          } else {
            throw new Error('Unable to complete login. Please try again.');
          }
      }
    }
  },

  signup: async (userData) => {
    try {
      const response = await api.post('/auth/signup', userData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  resetPassword: async (email) => {
    try {
      const response = await api.post('/auth/password/reset/request', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  verifyReset: async (email, code, newPassword) => {
    try {
      const payload = {
        code,
        new_password: newPassword
      };
      if (email) {
        payload.email = email;
      }
      const response = await api.post('/auth/password/reset/verify', payload);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  setupMFA: async (bearerToken) => {
    try {
      const response = await api.get('/auth/mfa/enable', bearerToken
        ? { headers: { 'Authorization': `Bearer ${bearerToken}` } }
        : undefined);
      // Validate response against MFASetupResponse schema
      const { status, message, qrcode, setup_token, error } = response.data;
      if (!status || !message || !qrcode || !setup_token) {
        throw new Error(error || 'Invalid MFA setup response');
      }
      if (status === 'error') {
        throw new Error(error || 'MFA setup failed');
      }
      return {
        status,
        message,
        qrcode,
        setup_token
      };
    } catch (error) {
      console.error('MFA setup error:', {
        message: error.message,
        response: error.response?.data
      });
      throw error.response?.data || error;
    }
  },

  verifyMFA: async (mfaData) => {
    try {
      // Get stored MFA token
      const mfaToken = sessionStorage.getItem('mfa_token');
      const { mfa_code } = mfaData;

      // Accept 6-digit TOTP codes and XXXX-XXXX-XXXX recovery codes
      const isRecovery = typeof mfa_code === 'string' && mfa_code.length === 14 && (mfa_code.match(/-/g) || []).length === 2;
      if (!mfaToken || !mfa_code || (!isRecovery && mfa_code.length !== 6)) {
        throw new Error('Invalid MFA verification data');
      }

      // Make the MFA verification request
      const response = await api.post('/auth/mfa/verify', { mfa_code }, {
        headers: {
          'Authorization': `Bearer ${mfaToken}`
        }
      });

      const { status, error } = response.data;

      // Check for error status
      if (status === 'error' || error) {
        throw new Error(error || 'MFA verification failed');
      }

      // Backend sets tokens via httpOnly cookies (no tokens in body).
      // Pending approval is also possible if user lost company access.
      return response.data;
    } catch (error) {
      console.error('MFA verification error:', {
        message: error.message,
        response: error.response?.data
      });
      throw error.response?.data || error;
    }
  },

  disableMFA: async (mfaCode) => {
    try {
      const response = await api.post('/auth/mfa/disable', { mfa_code: mfaCode });
      return response.data;
    } catch (error) {
      console.error('Failed to disable MFA:', error);
      throw error.response?.data || error;
    }
  },

  completeMFASetup: async (setupData, bearerToken) => {
    try {
      // Validate input against MFASetup schema
      const { setup_token, mfa_code } = setupData;
      if (!setup_token || !mfa_code || mfa_code.length !== 6) {
        throw new Error('Invalid MFA setup completion data');
      }

      const response = await api.post('/auth/mfa/enable', setupData, bearerToken
        ? { headers: { 'Authorization': `Bearer ${bearerToken}` } }
        : undefined);
      // Validate response against MessageResponse schema
      const { status, message, error, recovery_codes } = response.data;
      if (!status || !message) {
        throw new Error('Invalid response format');
      }
      if (status === 'error') {
        throw new Error(error || 'MFA setup completion failed');
      }
      return {
        status,
        message,
        recovery_codes
      };
    } catch (error) {
      console.error('MFA setup completion error:', {
        message: error.message,
        response: error.response?.data
      });
      throw error.response?.data || error;
    }
  },

  adminRegenerateRecoveryCodes: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/mfa/recovery-codes/regenerate`);
      return response.data;
    } catch (error) {
      console.error('Failed to regenerate recovery codes:', error);
      throw error.response?.data || error;
    }
  },

  // List current user's active sessions
  listSessions: async () => {
    try {
      const response = await api.get('/auth/sessions');
      return response.data;
    } catch (error) {
      console.error('Failed to list sessions:', error);
      throw error.response?.data || error;
    }
  },

  // Revoke one of your own sessions by DB ID
  revokeSession: async (sessionDbId) => {
    try {
      const response = await api.delete(`/auth/sessions/${sessionDbId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to revoke session:', error);
      throw error.response?.data || error;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/password/change', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // WebAuthn / Security Key endpoints
  getWebAuthnRegisterOptions: async (bearerToken) => {
    const response = await api.get('/auth/webauthn/register/options', bearerToken
      ? { headers: { 'Authorization': `Bearer ${bearerToken}` } }
      : undefined);
    return response.data;
  },

  verifyWebAuthnRegistration: async (credential, deviceName, bearerToken) => {
    const response = await api.post('/auth/webauthn/register/verify', {
      credential,
      device_name: deviceName,
    }, bearerToken
      ? { headers: { 'Authorization': `Bearer ${bearerToken}` } }
      : undefined);
    return response.data;
  },

  getWebAuthnAuthenticateOptions: async (mfaToken) => {
    const response = await api.post('/auth/webauthn/authenticate/options', {}, {
      headers: { 'Authorization': `Bearer ${mfaToken}` },
    });
    return response.data;
  },

  verifyWebAuthnAuthentication: async (credential, mfaToken) => {
    const response = await api.post('/auth/webauthn/authenticate/verify', {
      credential,
    }, {
      headers: { 'Authorization': `Bearer ${mfaToken}` },
    });
    return response.data;
  },

  getWebAuthnKeys: async () => {
    const response = await api.get('/auth/webauthn/keys');
    return response.data;
  },

  renameWebAuthnKey: async (keyId, deviceName) => {
    const response = await api.put(`/auth/webauthn/keys/${keyId}`, {
      device_name: deviceName,
    });
    return response.data;
  },

  deleteWebAuthnKey: async (keyId) => {
    const response = await api.delete(`/auth/webauthn/keys/${keyId}`);
    return response.data;
  },

  adminGetWebAuthnKeys: async (userId) => {
    const response = await api.get(`/auth/webauthn/admin/users/${userId}/keys`);
    return response.data;
  },

  adminRevokeWebAuthnKey: async (userId, keyId) => {
    const response = await api.delete(`/auth/webauthn/admin/users/${userId}/keys/${keyId}`);
    return response.data;
  },
};

// =============================================================================
// Application Management API (OAuth 2.0 / OIDC)
// =============================================================================
export const applicationAPI = {
  // List all applications
  list: async () => {
    try {
      const response = await api.get('/admin/applications');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch applications');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to list applications:', error);
      throw error.response?.data || error;
    }
  },

  // Get application details
  get: async (appId) => {
    try {
      const response = await api.get(`/admin/applications/${appId}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch application');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get application:', error);
      throw error.response?.data || error;
    }
  },

  // Create new application
  create: async (appData) => {
    try {
      const response = await api.post('/admin/applications', appData);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to create application');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to create application:', error);
      throw error.response?.data || error;
    }
  },

  // Update application
  update: async (appId, appData) => {
    try {
      const response = await api.put(`/admin/applications/${appId}`, appData);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update application');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update application:', error);
      throw error.response?.data || error;
    }
  },

  // Delete application
  delete: async (appId) => {
    try {
      const response = await api.delete(`/admin/applications/${appId}`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to delete application');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to delete application:', error);
      throw error.response?.data || error;
    }
  },

  // Rotate client secret
  rotateSecret: async (appId) => {
    try {
      const response = await api.post(`/admin/applications/${appId}/rotate-secret`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to rotate secret');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to rotate secret:', error);
      throw error.response?.data || error;
    }
  },

  // Upload application logo
  uploadLogo: async (appId, formData) => {
    try {
      const response = await api.post(`/admin/applications/${appId}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to upload logo');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to upload logo:', error);
      throw error.response?.data || error;
    }
  },

  // Delete application logo
  deleteLogo: async (appId) => {
    try {
      const response = await api.delete(`/admin/applications/${appId}/logo`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to delete logo');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to delete logo:', error);
      throw error.response?.data || error;
    }
  },

  // Get effective users who have access via RBAC
  getEffectiveUsers: async (appId) => {
    try {
      const response = await api.get(`/admin/applications/${appId}/effective-users`);
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch effective users');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get effective users:', error);
      throw error.response?.data || error;
    }
  },

  // OIDC: Get configuration
  getOidcConfig: async (appId) => {
    try {
      const response = await api.get(`/admin/applications/${appId}/oidc/config`);
      return response.data;
    } catch (error) {
      console.error('Failed to get OIDC config:', error);
      throw error.response?.data || error;
    }
  },

  // OIDC: Update configuration
  updateOidcConfig: async (appId, config) => {
    try {
      const response = await api.put(`/admin/applications/${appId}/oidc/config`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to update OIDC config:', error);
      throw error.response?.data || error;
    }
  },

  // OIDC: Generate credentials (first time)
  generateOidcCredentials: async (appId) => {
    try {
      const response = await api.post(`/admin/applications/${appId}/oidc/credentials`);
      return response.data;
    } catch (error) {
      console.error('Failed to generate OIDC credentials:', error);
      throw error.response?.data || error;
    }
  },

  // OIDC: Regenerate client secret
  regenerateOidcSecret: async (appId) => {
    try {
      const response = await api.post(`/admin/applications/${appId}/oidc/credentials/regenerate`);
      return response.data;
    } catch (error) {
      console.error('Failed to regenerate OIDC secret:', error);
      throw error.response?.data || error;
    }
  },

  // Role Access — get roles assigned to this application
  getRoles: async (appId) => {
    try {
      const response = await api.get(`/admin/applications/${appId}/roles`);
      return response.data;
    } catch (error) {
      console.error('Failed to get application roles:', error);
      throw error.response?.data || error;
    }
  },

  // Role Access — set which roles can access this application
  setRoles: async (appId, roleIds) => {
    try {
      const response = await api.put(`/admin/applications/${appId}/roles`, { role_ids: roleIds });
      return response.data;
    } catch (error) {
      console.error('Failed to set application roles:', error);
      throw error.response?.data || error;
    }
  },

  // Role Access — get default settings for a role on this app
  getRoleSettings: async (appId, roleId) => {
    try {
      const response = await api.get(`/admin/applications/${appId}/roles/${roleId}/settings`);
      return response.data;
    } catch (error) {
      console.error('Failed to get role settings:', error);
      throw error.response?.data || error;
    }
  },

  // Role Access — update default settings for a role on this app
  updateRoleSettings: async (appId, roleId, settings) => {
    try {
      const response = await api.put(`/admin/applications/${appId}/roles/${roleId}/settings`, { settings });
      return response.data;
    } catch (error) {
      console.error('Failed to update role settings:', error);
      throw error.response?.data || error;
    }
  },

};

// =============================================================================
// User Applications API (for end users)
// =============================================================================
export const userApplicationAPI = {
  // Get applications assigned to current user
  getMyApplications: async () => {
    try {
      const response = await api.get('/user/applications');
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch applications');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to get my applications:', error);
      throw error.response?.data || error;
    }
  }
};

// =============================================================================
// SAML IdP API
// =============================================================================
export const samlAPI = {
  // Certificate Management
  listCertificates: async () => {
    const response = await api.get('/saml/certificates');
    return response.data;
  },
  
  createCertificate: async (data) => {
    const response = await api.post('/saml/certificates', data);
    return response.data;
  },
  
  activateCertificate: async (certId) => {
    const response = await api.post(`/saml/certificates/${certId}/activate`);
    return response.data;
  },
  
  downloadCertificate: async (certId, type = 'public', password = null) => {
    let url = `/saml/certificates/${certId}/download?type=${type}`;
    if (type === 'pfx' && password) {
      url += `&password=${encodeURIComponent(password)}`;
    }
    const response = await api.get(url, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  deleteCertificate: async (certId) => {
    const response = await api.delete(`/saml/certificates/${certId}`);
    return response.data;
  },
  
  // SP Metadata Management
  getSPMetadata: async (appId) => {
    const response = await api.get(`/saml/applications/${appId}/sp-metadata`);
    return response.data;
  },
  
  updateSPMetadata: async (appId, data) => {
    const response = await api.post(`/saml/applications/${appId}/sp-metadata`, data);
    return response.data;
  },
  
  // SAML Configuration Management
  getSAMLConfig: async (appId) => {
    const response = await api.get(`/saml/applications/${appId}/config`);
    return response.data;
  },
  
  updateSAMLConfig: async (appId, data) => {
    const response = await api.post(`/saml/applications/${appId}/config`, data);
    return response.data;
  },
  
  // Auto-detect metadata URL
  autoDetectMetadataUrl: async (appId, data) => {
    const response = await api.post(`/saml/applications/${appId}/auto-detect-metadata`, data);
    return response.data;
  },
  
  // Fetch and parse SP metadata
  fetchSPMetadata: async (appId, data) => {
    const response = await api.post(`/saml/applications/${appId}/fetch-sp-metadata`, data);
    return response.data;
  },

  // Upload SP metadata XML file
  uploadSPMetadata: async (appId, file) => {
    const formData = new FormData();
    formData.append('metadata_file', file);

    const response = await api.post(`/saml/applications/${appId}/upload-sp-metadata`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  // Metadata Download
  getIdPMetadata: async (appId) => {
    const response = await fetch(`${API_BASE_URL}/saml/metadata/${appId}`, {
      credentials: 'include'
    });
    return await response.text();
  }
};

// Company API
export const companyAPI = {
  // List all companies
  listCompanies: async (params = {}) => {
    try {
      const response = await api.get('/companies', { params });
      return response.data;
    } catch (error) {
      logger.error('Failed to list companies:', error);
      throw error;
    }
  },

  // Get a specific company
  getCompany: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get company ${companyId}:`, error);
      throw error;
    }
  },

  // Create a new company
  createCompany: async (companyData) => {
    try {
      const response = await api.post('/companies', companyData);
      return response.data;
    } catch (error) {
      logger.error('Failed to create company:', error);
      throw error;
    }
  },

  // Update a company
  updateCompany: async (companyId, companyData) => {
    try {
      const response = await api.put(`/companies/${companyId}`, companyData);
      return response.data;
    } catch (error) {
      logger.error(`Failed to update company ${companyId}:`, error);
      throw error;
    }
  },

  // Delete a company
  deleteCompany: async (companyId) => {
    try {
      const response = await api.delete(`/companies/${companyId}`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to delete company ${companyId}:`, error);
      throw error;
    }
  },

  // Activate a company
  activateCompany: async (companyId) => {
    try {
      const response = await api.post(`/companies/${companyId}/activate`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to activate company ${companyId}:`, error);
      throw error;
    }
  },

  // Get all users in a company
  getCompanyUsers: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}/users`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get users for company ${companyId}:`, error);
      throw error;
    }
  },

  // Get allowed roles for a company
  getAllowedRoles: async (companyId) => {
    try {
      const response = await api.get(`/admin/companies/${companyId}/allowed-roles`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get allowed roles for company ${companyId}:`, error);
      throw error;
    }
  },

  // Set allowed roles for a company (replaces all)
  setAllowedRoles: async (companyId, roleIds) => {
    try {
      const response = await api.put(`/admin/companies/${companyId}/allowed-roles`, { role_ids: roleIds });
      return response.data;
    } catch (error) {
      logger.error(`Failed to set allowed roles for company ${companyId}:`, error);
      throw error;
    }
  },

  // Set lockout policy for a company
  setLockoutPolicy: async (companyId, policy) => {
    try {
      const response = await api.put(`/admin/companies/${companyId}/lockout-policy`, policy);
      return response.data;
    } catch (error) {
      logger.error(`Failed to set lockout policy for company ${companyId}:`, error);
      throw error;
    }
  },

  // Set session policy (max_concurrent_sessions) for a company
  setSessionPolicy: async (companyId, maxConcurrentSessions) => {
    try {
      const response = await api.put(`/admin/companies/${companyId}/session-policy`, {
        max_concurrent_sessions: maxConcurrentSessions
      });
      return response.data;
    } catch (error) {
      logger.error(`Failed to set session policy for company ${companyId}:`, error);
      throw error;
    }
  },

  // Admin: list active sessions for a user
  adminListUserSessions: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/sessions`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to list sessions for user ${userId}:`, error);
      throw error;
    }
  },

  // Admin: revoke a specific session for a user
  adminRevokeUserSession: async (userId, sessionDbId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/sessions/${sessionDbId}`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to revoke session ${sessionDbId} for user ${userId}:`, error);
      throw error;
    }
  },

  // Get allowed service types for a company
  getServiceTypes: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}/service-types`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get service types for company ${companyId}:`, error);
      throw error;
    }
  },

  // Set allowed service types for a company
  setServiceTypes: async (companyId, serviceTypes) => {
    try {
      const response = await api.put(`/companies/${companyId}/service-types`, { service_types: serviceTypes });
      return response.data;
    } catch (error) {
      logger.error(`Failed to set service types for company ${companyId}:`, error);
      throw error;
    }
  },

  // Get all available service types
  listAllServiceTypes: async () => {
    try {
      const response = await api.get('/companies/service-types');
      return response.data;
    } catch (error) {
      logger.error('Failed to list service types:', error);
      throw error;
    }
  },

  // Service Instances
  listServiceInstances: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}/service-instances`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to list service instances for company ${companyId}:`, error);
      throw error;
    }
  },

  createServiceInstance: async (companyId, data) => {
    try {
      const response = await api.post(`/companies/${companyId}/service-instances`, data);
      return response.data;
    } catch (error) {
      logger.error(`Failed to create service instance:`, error);
      throw error;
    }
  },

  updateServiceInstance: async (companyId, instanceId, data) => {
    try {
      const response = await api.put(`/companies/${companyId}/service-instances/${instanceId}`, data);
      return response.data;
    } catch (error) {
      logger.error(`Failed to update service instance ${instanceId}:`, error);
      throw error;
    }
  },

  deleteServiceInstance: async (companyId, instanceId) => {
    try {
      const response = await api.delete(`/companies/${companyId}/service-instances/${instanceId}`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to delete service instance ${instanceId}:`, error);
      throw error;
    }
  }
};
// =============================================================================
export const categoryAPI = {
  // Get categories with user's assigned applications (for dashboard)
  getCategories: async () => {
    try {
      const response = await api.get('/categories');
      return response.data;
    } catch (error) {
      logger.error('Failed to get categories:', error);
      throw error;
    }
  },

  // Get all categories with ALL applications (for admin management)
  getCategoriesAdmin: async () => {
    try {
      const response = await api.get('/categories/admin');
      return response.data;
    } catch (error) {
      logger.error('Failed to get categories (admin):', error);
      throw error;
    }
  },

  // Create a new category
  createCategory: async (categoryData) => {
    try {
      const response = await api.post('/categories', categoryData);
      return response.data;
    } catch (error) {
      logger.error('Failed to create category:', error);
      throw error;
    }
  },

  // Update a category
  updateCategory: async (categoryId, categoryData) => {
    try {
      const response = await api.put(`/categories/${categoryId}`, categoryData);
      return response.data;
    } catch (error) {
      logger.error(`Failed to update category ${categoryId}:`, error);
      throw error;
    }
  },

  // Delete a category
  deleteCategory: async (categoryId) => {
    try {
      const response = await api.delete(`/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to delete category ${categoryId}:`, error);
      throw error;
    }
  },

  // Reorder categories
  reorderCategories: async (order) => {
    try {
      const response = await api.post('/categories/reorder', { order });
      return response.data;
    } catch (error) {
      logger.error('Failed to reorder categories:', error);
      throw error;
    }
  },

  // Assign app to category
  assignAppToCategory: async (categoryId, applicationId) => {
    try {
      const response = await api.post(`/categories/${categoryId}/applications`, { application_id: applicationId });
      return response.data;
    } catch (error) {
      logger.error(`Failed to assign app to category:`, error);
      throw error;
    }
  },

  // Reorder apps in category
  reorderAppsInCategory: async (categoryId, order) => {
    try {
      const response = await api.post(`/categories/${categoryId}/reorder-apps`, { order });
      return response.data;
    } catch (error) {
      logger.error('Failed to reorder apps:', error);
      throw error;
    }
  }
};

// =============================================================================
// Favorites API
// =============================================================================
export const favoritesAPI = {
  // Get user's favorites
  getFavorites: async () => {
    try {
      const response = await api.get('/favorites');
      return response.data;
    } catch (error) {
      logger.error('Failed to get favorites:', error);
      throw error;
    }
  },

  // Add to favorites
  addFavorite: async (applicationId) => {
    try {
      const response = await api.post('/favorites', { application_id: applicationId });
      return response.data;
    } catch (error) {
      logger.error('Failed to add favorite:', error);
      throw error;
    }
  },

  // Remove from favorites
  removeFavorite: async (applicationId) => {
    try {
      const response = await api.delete(`/favorites/${applicationId}`);
      return response.data;
    } catch (error) {
      logger.error('Failed to remove favorite:', error);
      throw error;
    }
  },

  // Toggle favorite
  toggleFavorite: async (applicationId) => {
    try {
      const response = await api.post(`/favorites/toggle/${applicationId}`);
      return response.data;
    } catch (error) {
      logger.error('Failed to toggle favorite:', error);
      throw error;
    }
  },

  // Reorder favorites
  reorderFavorites: async (order) => {
    try {
      const response = await api.post('/favorites/reorder', { order });
      return response.data;
    } catch (error) {
      logger.error('Failed to reorder favorites:', error);
      throw error;
    }
  }
};

// =============================================================================
// Setting Definitions API
// =============================================================================
export const settingDefinitionsAPI = {
  // Get setting definitions for an application
  getDefinitions: async (appId) => {
    try {
      const response = await api.get(`/applications/${appId}/setting-definitions`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get setting definitions for app ${appId}:`, error);
      throw error;
    }
  }
};

// =============================================================================
// Role API
// =============================================================================
export const roleAPI = {
  // Get all roles
  getRoles: async (includeApplications = false, includeSettings = false) => {
    try {
      const params = new URLSearchParams();
      if (includeApplications) params.append('include_applications', 'true');
      if (includeSettings) params.append('include_settings', 'true');
      const response = await api.get(`/roles?${params.toString()}`);
      return response.data;
    } catch (error) {
      logger.error('Failed to get roles:', error);
      throw error;
    }
  },

  // Get a specific role
  getRole: async (roleId) => {
    try {
      const response = await api.get(`/roles/${roleId}`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get role ${roleId}:`, error);
      throw error;
    }
  },

  // Get effective applications and settings for a role (including inherited)
  getRoleEffective: async (roleId) => {
    try {
      const response = await api.get(`/roles/${roleId}/effective`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get effective role ${roleId}:`, error);
      throw error;
    }
  },

  // Create a new role
  createRole: async (roleData) => {
    try {
      const response = await api.post('/roles', roleData);
      return response.data;
    } catch (error) {
      logger.error('Failed to create role:', error);
      throw error;
    }
  },

  // Update a role
  updateRole: async (roleId, roleData) => {
    try {
      const response = await api.put(`/roles/${roleId}`, roleData);
      return response.data;
    } catch (error) {
      logger.error(`Failed to update role ${roleId}:`, error);
      throw error;
    }
  },

  // Delete a role
  deleteRole: async (roleId) => {
    try {
      const response = await api.delete(`/roles/${roleId}`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to delete role ${roleId}:`, error);
      throw error;
    }
  },

  // Get applications for a role
  getRoleApplications: async (roleId) => {
    try {
      const response = await api.get(`/roles/${roleId}/applications`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get role applications:`, error);
      throw error;
    }
  },

  // Set applications for a role (bulk update)
  setRoleApplications: async (roleId, applicationIds) => {
    try {
      const response = await api.put(`/roles/${roleId}/applications/bulk`, { application_ids: applicationIds });
      return response.data;
    } catch (error) {
      logger.error(`Failed to set role applications:`, error);
      throw error;
    }
  },

  // Get users assigned to a role
  getRoleUsers: async (roleId) => {
    try {
      const response = await api.get(`/roles/${roleId}/users`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get role users:`, error);
      throw error;
    }
  },

  // Note: User role assignment/removal is now per-company via adminAPI.setUserCompanyRoles()

  // Get user's applications from roles
  getUserRoleApplications: async (userId) => {
    try {
      const response = await api.get(`/roles/user/${userId}/applications`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get user role applications:`, error);
      throw error;
    }
  },

  // Get settings for an application within a role
  getRoleAppSettings: async (roleId, appId) => {
    try {
      const response = await api.get(`/roles/${roleId}/applications/${appId}/settings`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to get role app settings:`, error);
      throw error;
    }
  },

  // Set all settings for an application within a role (bulk)
  setRoleAppSettings: async (roleId, appId, settings) => {
    try {
      const response = await api.put(`/roles/${roleId}/applications/${appId}/settings/bulk`, { settings });
      return response.data;
    } catch (error) {
      logger.error(`Failed to set role app settings:`, error);
      throw error;
    }
  }
};

// =============================================================================
// System Logs API (Admin)
// =============================================================================
export const systemLogsAPI = {
  getLogs: async ({ levels, logger, search, hours, limit, offset } = {}) => {
    try {
      const params = new URLSearchParams();
      if (levels) params.set('levels', levels);
      if (logger) params.set('logger', logger);
      if (search) params.set('search', search);
      if (hours) params.set('hours', hours);
      if (limit) params.set('limit', limit);
      if (offset !== undefined) params.set('offset', offset);
      const response = await api.get(`/admin/system-logs?${params}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch system logs:', error);
      throw error;
    }
  },
  getStats: async () => {
    try {
      const response = await api.get('/admin/system-logs/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch log stats:', error);
      throw error;
    }
  },
  getDownloadUrl: () => '/api/admin/system-logs/download',
};

// =============================================================================
// API Key Management (Admin)
// =============================================================================
export const apiKeyAPI = {
  list: async () => {
    try {
      const response = await api.get('/admin/api-keys');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      throw error.response?.data || error;
    }
  },
  create: async (name, scopes = 'register') => {
    try {
      const response = await api.post('/admin/api-keys', { name, scopes });
      return response.data;
    } catch (error) {
      console.error('Failed to create API key:', error);
      throw error.response?.data || error;
    }
  },
  revoke: async (keyId) => {
    try {
      const response = await api.delete(`/admin/api-keys/${keyId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      throw error.response?.data || error;
    }
  },
};

// =============================================================================
// Team Management API (Delegated Administration — client-facing)
// =============================================================================
export const teamAPI = {
  // List users in the current user's company
  listUsers: async (companyId) => {
    try {
      const response = await api.get(`/company/${companyId}/users`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get allowed roles for the company
  getRoles: async (companyId) => {
    try {
      const response = await api.get(`/company/${companyId}/roles`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Invite / add a user to the company
  inviteUser: async (companyId, { email, first_name, last_name, role_ids, send_invite = true }) => {
    try {
      const response = await api.post(`/company/${companyId}/users`, {
        email, first_name, last_name, role_ids, send_invite,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Remove a user from the company
  removeUser: async (companyId, userId) => {
    try {
      const response = await api.delete(`/company/${companyId}/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get a user's roles in the company
  getUserRoles: async (companyId, userId) => {
    try {
      const response = await api.get(`/company/${companyId}/users/${userId}/roles`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Set a user's roles in the company
  setUserRoles: async (companyId, userId, roleIds) => {
    try {
      const response = await api.put(`/company/${companyId}/users/${userId}/roles`, {
        role_ids: roleIds,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

// =============================================================================
// Onboarding Tasks API
// =============================================================================
export const tasksAPI = {
  // List tasks
  listTasks: async ({ companyId, status, assignedToMe } = {}) => {
    try {
      const params = new URLSearchParams();
      if (companyId) params.append('company_id', companyId);
      if (status) params.append('status', status);
      if (assignedToMe) params.append('assigned_to_me', 'true');
      
      const response = await api.get(`/tasks?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get single task
  getTask: async (taskId) => {
    try {
      const response = await api.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Create task
  createTask: async (taskData) => {
    try {
      const response = await api.post('/tasks', taskData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Update task
  updateTask: async (taskId, updates) => {
    try {
      const response = await api.patch(`/tasks/${taskId}`, updates);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Delete task
  deleteTask: async (taskId) => {
    try {
      const response = await api.delete(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get company task summary (progress stats)
  getCompanySummary: async (companyId) => {
    try {
      const response = await api.get(`/tasks/company/${companyId}/summary`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Mark task as completed
  completeTask: async (taskId) => {
    try {
      const response = await api.patch(`/tasks/${taskId}`, { status: 'completed' });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Dismiss task
  dismissTask: async (taskId) => {
    try {
      const response = await api.patch(`/tasks/${taskId}`, { status: 'dismissed' });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default api;
